import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { UserListItem, UserRole } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Select } from "../components/Select";
import { useT } from "../i18n";

export function UsersPage() {
  const { user: currentUser } = useAuth();
  const { t } = useT();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form state
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState<UserRole>("viewer");
  const [creating, setCreating] = useState(false);

  // Delete dialog state
  const [deleteTarget, setDeleteTarget] = useState<UserListItem | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  async function loadUsers() {
    setLoading(true);
    try {
      const res = await api.listUsers(100);
      setUsers(res.data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail || !newPassword) return;
    setError(null);
    setCreating(true);
    try {
      await api.createUser({ email: newEmail, password: newPassword, role: newRole });
      setNewEmail("");
      setNewPassword("");
      setNewRole("viewer");
      await loadUsers();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteUser(deleteTarget.id);
      setDeleteTarget(null);
      await loadUsers();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setDeleting(false);
    }
  }

  if (currentUser?.role !== "admin") {
    return (
      <div className="px-4 py-10 text-center text-brand-muted">
        {t("users.onlyAdmin")}
      </div>
    );
  }

  return (
    <div className="px-4 py-4 max-w-3xl mx-auto">
      <h1 className="text-xl font-semibold mb-4">{t("users.title")}</h1>

      {error && <div className="text-red-400 mb-4 text-sm">{error}</div>}

      {/* Create user form */}
      <div className="mb-8 p-4 rounded-xl bg-brand-surface border border-brand-line">
        <h2 className="text-sm font-semibold mb-3">{t("users.addNew")}</h2>
        <form onSubmit={handleCreate} className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <input
            type="email"
            placeholder={t("users.email")}
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            className="px-3 py-2 rounded bg-brand-dark border border-brand-line focus:border-brand-red outline-none text-sm"
            required
            disabled={creating}
          />
          <input
            type="text"
            placeholder={t("users.password")}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="px-3 py-2 rounded bg-brand-dark border border-brand-line focus:border-brand-red outline-none text-sm"
            required
            disabled={creating}
          />
          <Select
            value={newRole}
            options={[
              { value: "viewer", label: t("users.role.viewer") },
              { value: "publisher", label: t("users.role.publisher") },
              { value: "admin", label: t("users.role.admin") },
            ]}
            onChange={(v) => setNewRole(v as UserRole)}
            disabled={creating}
            size="sm"
          />
          <button
            type="submit"
            disabled={creating || !newEmail || !newPassword}
            className="px-4 py-2 rounded bg-brand-red hover:bg-red-700 disabled:opacity-40 text-sm font-medium"
          >
            {creating ? t("common.adding") : t("users.addUser")}
          </button>
        </form>
      </div>

      {/* User list */}
      <div>
        <h2 className="text-sm font-semibold mb-3">{t("users.users", { n: users.length })}</h2>
        {loading ? (
          <p className="text-brand-muted">{t("common.loading")}</p>
        ) : (
          <div className="space-y-2">
            {users.map((u) => (
              <div
                key={u.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-brand-surface border border-brand-line"
              >
                <div className="w-8 h-8 rounded-full bg-brand-red flex items-center justify-center text-sm font-semibold shrink-0">
                  {u.email.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{u.email}</div>
                  <div className="text-xs text-brand-muted capitalize">{t(`users.role.${u.role}`)}</div>
                </div>
                {u.id !== currentUser?.id && (
                  <button
                    onClick={() => setDeleteTarget(u)}
                    className="px-3 py-1 rounded text-xs bg-brand-dark hover:bg-red-900/40 border border-brand-line text-red-400"
                  >
                    {t("common.delete")}
                  </button>
                )}
                {u.id === currentUser?.id && (
                  <span className="text-xs text-brand-muted">{t("common.you")}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title={t("users.deleteTitle")}
        message={t("users.deleteMessage", { email: deleteTarget?.email ?? "" })}
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}